/**
 * Finding phone numbers inside message text.
 *
 * A number somebody types into a chat is the commonest thing in a
 * messaging app that you want to act on rather than read: you want to
 * message them, or invite them if they are not here yet.
 *
 * Deliberately conservative. A false positive turns an ordinary number —
 * a price, a date, an order reference — into a tappable thing that leads
 * nowhere useful, which is worse than missing one.
 */

/**
 * Candidate runs of digits that could be dialled.
 *
 * Requires either a leading + or at least 7 digits, and allows the
 * spaces, dashes and brackets people actually type. The lookarounds stop
 * it matching the middle of a longer number or a word like "abc1234567".
 */
const PHONE = /(?<![\w+])(\+?\d[\d\s()\-.]{6,18}\d)(?![\w])/g

/**
 * Split text into plain strings and phone numbers.
 *
 *   segments('call 08012345678 now')
 *   // ['call ', { phone: '08012345678' }, ' now']
 *
 * Returns segments rather than HTML so each renderer decides what a
 * number becomes — a button on the web, a Text with onPress on mobile.
 * Nothing here produces markup, because this file is bundled by both
 * Vite and Metro.
 *
 * @param {string} text
 * @returns {Array<string | { phone: string }>}
 */
export function segments (text) {
  const value = String(text ?? '')
  if (!value) return []

  const out = []
  let last = 0

  for (const match of value.matchAll(PHONE)) {
    const raw = match[1]

    // Count digits, not characters: "1234 567" is seven digits with a
    // space in it, and "+1 (555) 010-9999" is eleven.
    const digits = raw.replace(/\D/g, '').length
    const plausible = raw.startsWith('+') ? digits >= 8 : digits >= 10

    if (!plausible) continue

    if (match.index > last) out.push(value.slice(last, match.index))
    out.push({ phone: raw.trim() })
    last = match.index + raw.length
  }

  if (last < value.length) out.push(value.slice(last))
  return out.length ? out : [value]
}

/** Does this text contain anything worth making tappable? */
export function hasPhone (text) {
  return segments(text).some(part => typeof part !== 'string')
}
